"use client";

import translations from "@/lib/translations.json";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  FileCheck,
  Filter,
  Globe,
  Heart,
  Landmark,
  MessageCircle,
  Receipt,
  Tag,
  TrendingUp,
  UserPlus,
  Wallet,
} from "lucide-react";
import Image from "next/image";
import { useWebsiteLang } from "../context/website-lang-context";

const features = [
  {
    icon: UserPlus,
    itemsIcons: [FileCheck, Filter, Tag, Clock, MessageCircle],
    color: "from-violet-500 to-purple-600",
    accent: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    mockup: "talent",
  },
  {
    icon: Heart,
    itemsIcons: [FileCheck, Heart, CalendarClock, Clock],
    color: "from-cyan-500 to-blue-600",
    accent: "text-cyan-400",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500/20",
    mockup: "patients",
  },
  {
    icon: CalendarClock,
    itemsIcons: [
      CalendarClock,
      Clock,
      AlertTriangle,
      CheckCircle2,
      AlertTriangle,
    ],
    color: "from-teal-500 to-emerald-600",
    accent: "text-teal-400",
    bg: "bg-teal-500/10",
    border: "border-teal-500/20",
    mockup: "schedule",
  },
  {
    icon: Wallet,
    itemsIcons: [Wallet, FileCheck, TrendingUp, Clock],
    color: "from-amber-500 to-orange-600",
    accent: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    mockup: "payments",
  },
  {
    icon: Receipt,
    itemsIcons: [Receipt, Globe, CheckCircle2, Clock, Filter],
    color: "from-orange-500 to-red-600",
    accent: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    mockup: "invoicing",
  },
  {
    icon: Landmark,
    itemsIcons: [Landmark, AlertTriangle, TrendingUp, FileCheck],
    color: "from-emerald-500 to-teal-600",
    accent: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    mockup: "reconciliation",
  },
] as const;

function FeatureMockup({ type }: { type: string }) {
  if (type === "talent") {
    return (
      <div
        id="feature-people"
        className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.1] sm:scale-100 lg:scale-[1.15]"
      >
        <div className="absolute left-[-2%] top-[-4%] h-[110%] w-[110%] -inset-8 rounded-3xl bg-gradient-to-br from-white/20 to-transparent transform rotate-[4deg]" />

        <div className="relative z-10 h-[105%] w-[105%] overflow-hidden rounded-2xl">
          <Image
            src="/images/website/features/people/main/details.jpg"
            alt="Talent database details"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover object-left-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#08183f]/10" />
        </div>
        <div className="absolute right-[-6%] bottom-[-14%] z-20 h-[100%] w-[30%] overflow-hidden rounded-2xl bg-white shadow-[0_0px_20px_rgba(0,0,0,0.30)] ring-1 ring-black/5">
          <Image
            src="/images/website/features/people/main/filter.jpg"
            alt="Talent database filters"
            fill
            sizes="(max-width: 1024px) 24vw, 12vw"
            className="object-cover object-top"
          />
        </div>
      </div>
    );
  }

  if (type === "patients") {
    return (
      <div
        id="feature-patients"
        className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.20] sm:scale-100 lg:scale-[1.10]"
      >
        <div className="absolute right-[5%] top-[0%] h-[110%] w-[100%] rounded-3xl bg-gradient-to-b from-white/20 to-transparent transform rotate-[-2deg]" />

        <div className="relative z-10 left-[-2%] h-[96%] w-[96%] overflow-hidden rounded-2xl bg-[#0a1531] shadow-[0_22px_70px_rgba(0,0,0,0.35)]">
          <Image
            src="/images/website/features/contracts/main/patient.jpg"
            alt="Patients and contracts template"
            fill
            sizes="(max-width: 1024px) 96vw, 46vw"
            className="object-cover object-left-top"
          />
        </div>
        <div className="absolute right-[5%] bottom-[-12%] z-20 h-[76%] w-[41%] overflow-hidden rounded-2xl bg-white shadow-[0_0px_20px_rgba(0,0,0,0.30)] ring-1 ring-black/10">
          <div className="pointer-events-none absolute inset-0 rounded-2xl border border-cyan-200/70 shadow-[0_0_0_1px_rgba(255,255,255,0.55),0_0_40px_rgba(34,211,238,0.16)]" />
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-white/55 via-transparent to-black/10" />
          <Image
            src="/images/website/features/contracts/satellite/invite.jpg"
            alt="Patients and contracts details"
            fill
            sizes="(max-width: 1024px) 36vw, 18vw"
            className="object-cover object-top"
          />
        </div>
      </div>
    );
  }

  if (type === "schedule") {
    return (
      <div
        id="feature-schedule"
        className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.1] sm:scale-100 lg:scale-[1.10]"
      >
        <div className="absolute left-[-2%] top-[-4%] h-[110%] w-[110%] -inset-8 rounded-3xl bg-gradient-to-bl from-white/20 to-transparent transform rotate-[2deg]" />

        <div className="relative z-10 h-[100%] w-[55%] overflow-hidden rounded-2xl bg-[#0a1531] shadow-[0_22px_70px_rgba(0,0,0,0.35)]">
          <Image
            src="/images/website/features/scheduling/main/template.jpg"
            alt="Scheduling template"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover object-left-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0a1531]/12" />
        </div>
        <div className="absolute right-[0%] bottom-[-18%] z-20 h-[100%] w-[50%] overflow-hidden rounded-2xl bg-white shadow-[0_0px_20px_rgba(0,0,0,0.30)] ring-1 ring-black/10">
          <div className="pointer-events-none absolute inset-0 rounded-2xl border border-cyan-200/70 shadow-[0_0_0_1px_rgba(255,255,255,0.55),0_0_40px_rgba(34,211,238,0.16)]" />
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-white/55 via-transparent to-black/10" />
          <Image
            src="/images/website/features/scheduling/main/details.jpg"
            alt="Scheduling details"
            fill
            sizes="(max-width: 1024px) 24vw, 12vw"
            className="object-cover object-top"
          />
        </div>
      </div>
    );
  }

  if (type === "payments") {
    return (
      <div
        id="feature-payments"
        className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.2] sm:scale-100 lg:scale-[1.10]"
      >
        <div className="absolute right-[0%] top-[2%] h-[110%] w-[100%] rounded-3xl bg-gradient-to-tr from-white/20 to-transparent transform rotate-[-2deg]" />

        <div className="relative z-10 left-[-2%] h-[100%] w-[100%] overflow-hidden rounded-2xl bg-[#0a1531] shadow-[0_22px_70px_rgba(0,0,0,0.35)]">
          <Image
            src="/images/website/features/payments/main/payment.jpg"
            alt="Payments"
            fill
            className="object-cover object-left-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0a1531]/12" />
        </div>
      </div>
    );
  }

  if (type === "invoicing") {
    return (
      <div
        id="feature-invoicing"
        className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.2] sm:scale-100 lg:scale-[1.10]"
      >
        <div className="absolute left-[-2%] top-[-2%] h-[105%] w-[105%] rounded-3xl bg-gradient-to-b from-white/20 to-white/5 transform rotate-[-4deg]" />

        <div className="relative z-10 left-[0%] top-[0%] h-[105%] w-[105%] overflow-hidden rounded-2xl bg-[#0a1531] shadow-[0_22px_70px_rgba(0,0,0,0.35)]">
          <Image
            src="/images/website/features/invoicing/main/list.jpg"
            alt="Invoicing list"
            fill
            className="object-cover object-left-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0a1531]/12" />
        </div>
      </div>
    );
  }

  return (
    <div
      id="feature-banking"
      className="relative isolate aspect-[869/498] w-full overflow-visible p-4 scroll-mt-24 sm:scroll-mt-32 lg:scroll-mt-40 scale-[1.2] sm:scale-100 lg:scale-[1.10]"
    >
      <div className="absolute right-[0%] top-[-2%] h-[105%] w-[105%] rounded-3xl bg-gradient-to-b from-white/20 to-transparent transform rotate-[3deg]" />

      <div className="relative z-10 right-[4%] h-[105%] w-[105%] overflow-hidden rounded-2xl bg-[#0a1531] shadow-[0_22px_70px_rgba(0,0,0,0.35)]">
        <Image
          src="/images/website/features/banking/main/process.jpg"
          alt="Bank reconciliation"
          fill
          className="object-cover object-left-top"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0a1531]/12" />
      </div>
    </div>
  );
}

export default function Features() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.features;

  return (
    <section id="features" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-4 py-1.5 text-xs font-medium text-amber-400 glass">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-slow" />
            {t.badge}
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t.titlePrefix}{" "}
            <span className="text-gradient-amber">{t.titleHighlight}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-white/50">
            {t.subtitle}
          </p>
        </div>

        <div className="space-y-32">
          {t.items.map((featureText, index) => {
            const feature = features[index];

            return (
              <div key={featureText.label}>
                <div className="p-8 sm:p-10 lg:p-12">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                      <div className="flex items-center gap-4 mb-6">
                        <div
                          className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shrink-0 shadow-lg`}
                        >
                          <feature.icon size={28} className="text-white" />
                        </div>

                        <div>
                          <span
                            className={`text-xs font-medium ${feature.accent} uppercase tracking-wider`}
                          >
                            {featureText.label}
                          </span>

                          <h3 className="text-xl sm:text-2xl font-bold text-white mt-1">
                            {featureText.title}
                          </h3>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        {featureText.items.map((itemText, itemIndex) => {
                          const BulletIcon = feature.itemsIcons[itemIndex];

                          return (
                            <div
                              key={itemText}
                              className="flex items-center gap-3 text-sm sm:text-base text-white/60"
                            >
                              <BulletIcon
                                size={16}
                                className={`${feature.accent} shrink-0`}
                              />

                              {itemText}
                            </div>
                          );
                        })}
                      </div>

                      <p className="text-sm text-white/40 italic">
                        {featureText.description}
                      </p>
                    </div>

                    <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                      <FeatureMockup type={feature.mockup} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
